import { useRef, useState } from "react";

import { AssistantThreadMessage } from "./RetailerAssistantPanel";

/**
 * Scripted "Your Assistant" brain shared by the PDP assistant prototypes.
 *
 * Owns the thread state and the scripted reply timing so multiple entry points
 * (the inline PDP pills, a docked rail, a floating FAB) can reuse one source of
 * truth. `open` starts a fresh thread when called from closed; calling it while
 * open continues the chat. `send` always appends to the current thread.
 */

export const ASSISTANT_PRODUCT = {
  name: "Lemongrass Cactus Soap",
  brand: "Nopalera",
  image:
    "https://cdn.faire.com/fastly/c0d94ced91be797cae35e175da1fe68e55c23ac3336ae0b63e1950899c17cc3d.jpeg?format=jpg&precrop=2531%2C2531%2Cx333%2Cy256%2Csafe&width=720",
};

const RESPONSE_DELAY_MS = 1100;

export interface RetailerAssistant {
  isOpen: boolean;
  messages: AssistantThreadMessage[];
  pending: boolean;
  autoFocusKey: number;
  /** Open the assistant. Optionally seed a question; opening from closed resets the thread. */
  open: (question?: string) => void;
  close: () => void;
  /** Append a user question to the current (open) thread. */
  send: (text: string) => void;
}

export const useRetailerAssistant = (): RetailerAssistant => {
  // Starts closed with an empty thread — a page refresh is a clean slate.
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<AssistantThreadMessage[]>([]);
  const [pending, setPending] = useState(false);
  const [autoFocusKey, setAutoFocusKey] = useState(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const respondTo = (question: string) => {
    setPending(true);
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = setTimeout(() => {
      setMessages((prev) => [...prev, buildAssistantReply(question)]);
      setPending(false);
    }, RESPONSE_DELAY_MS);
  };

  const send = (text: string) => {
    setMessages((prev) => [...prev, { role: "user", text }]);
    respondTo(text);
  };

  const open = (question?: string) => {
    if (!isOpen) {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      setPending(false);
      setMessages([]);
      setIsOpen(true);
      if (question) {
        setMessages([{ role: "user", text: question }]);
        respondTo(question);
      } else {
        setAutoFocusKey((key) => key + 1);
      }
      return;
    }

    if (question) {
      send(question);
    } else {
      setAutoFocusKey((key) => key + 1);
    }
  };

  const close = () => {
    setIsOpen(false);
  };

  return { isOpen, messages, pending, autoFocusKey, open, close, send };
};

// ── Scripted assistant replies ─────────────────────────────────────────────

const MARGIN_FOLLOW_UPS = [
  "What's the brand minimum?",
  "How long until it ships?",
  "How many units are in a case?",
];

const SCRIPT: Record<string, AssistantThreadMessage> = {
  "what's the margin on this product?": {
    role: "assistant",
    researched: true,
    segments: [
      {
        kind: "paragraph",
        content: (
          <>
            At the suggested retail price, your gross margin is{" "}
            <strong>50%</strong>.
          </>
        ),
      },
      {
        kind: "list",
        items: [
          <>
            <strong>Wholesale: $7.00</strong>
          </>,
          <>
            <strong>Suggested retail: $14.00</strong>
          </>,
          <>
            <strong>Gross margin: 50%</strong>
          </>,
        ],
      },
    ],
    followUps: MARGIN_FOLLOW_UPS,
  },
  "what's unique about this brand?": {
    role: "assistant",
    researched: true,
    segments: [
      {
        kind: "paragraph",
        content: (
          <>
            <strong>Nopalera</strong> is a women-owned brand from San Antonio,
            Texas that celebrates Mexican heritage through cactus-based
            botanical skincare.
          </>
        ),
      },
      {
        kind: "paragraph",
        content: (
          <>
            They&rsquo;re a <strong>Top Shop</strong> with a{" "}
            <strong>4.9 rating</strong> across 50 reviews, and retailers
            consistently note how strongly customers connect with the
            brand&rsquo;s cultural story.
          </>
        ),
      },
    ],
    followUps: [
      "What's the margin on this product?",
      "How many units are in a case?",
      "What's the brand minimum?",
    ],
  },
  "how many units are in a case?": {
    role: "assistant",
    segments: [
      {
        kind: "paragraph",
        content: (
          <>
            This product ships in a <strong>Case of 8</strong> at{" "}
            <strong>$7.00 per unit</strong>, so one case is{" "}
            <strong>$56.00</strong>.
          </>
        ),
      },
      {
        kind: "paragraph",
        content: <>You can also add a tester for $3.50 at checkout.</>,
      },
    ],
    followUps: [
      "What's the margin on this product?",
      "What's unique about this brand?",
      "How long until it ships?",
    ],
  },
  "what's the brand minimum?": {
    role: "assistant",
    segments: [
      {
        kind: "paragraph",
        content: (
          <>
            Nopalera&rsquo;s order minimum is <strong>$150</strong>. A single
            case of this soap is $56.00, so you&rsquo;d add{" "}
            <strong>$94.00</strong> more to reach it.
          </>
        ),
      },
    ],
    followUps: [
      "What's the margin on this product?",
      "How long until it ships?",
      "What's unique about this brand?",
    ],
  },
  "how long until it ships?": {
    role: "assistant",
    segments: [
      {
        kind: "paragraph",
        content: (
          <>
            Based on the brand&rsquo;s actual shipment history, it typically
            ships in <strong>4 to 7 days</strong>. The current estimated
            delivery is <strong>Jun 21 - Jul 1</strong>.
          </>
        ),
      },
    ],
    followUps: [
      "What's the margin on this product?",
      "What's the brand minimum?",
      "How many units are in a case?",
    ],
  },
};

const buildAssistantReply = (question: string): AssistantThreadMessage => {
  const scripted = SCRIPT[question.trim().toLowerCase()];
  if (scripted) {
    return scripted;
  }
  return {
    role: "assistant",
    segments: [
      {
        kind: "paragraph",
        content: (
          <>
            Here&rsquo;s what I can tell you about{" "}
            <strong>{ASSISTANT_PRODUCT.name}</strong> from{" "}
            {ASSISTANT_PRODUCT.brand}. Ask me about margin, case sizes,
            shipping, or the brand minimum and I&rsquo;ll pull the details for
            your store.
          </>
        ),
      },
    ],
    followUps: MARGIN_FOLLOW_UPS,
  };
};
